import { useMemo } from 'react'
import store from 'store2'
import { SWRConfiguration } from 'swr'

import useAxios, { axiosInstance } from './useAxios'

import { JWT_TOKEN_NAME, REFRESH_TOKEN_NAME } from '../constants/auth-key'
import { IUser } from '../types/user'
import { IMerchant } from '../types/merchant'
import { sendToErrorLog } from '../utils/logs'
import {
  getMerchantCache,
  getUserCache,
  setMerchantCache,
  setUserCache,
} from '../utils/cache'

export const useUser = (revalidateOnFocus: boolean = true) => {
  const isAuthenticated = useMemo(() => {
    return !!store.get(JWT_TOKEN_NAME)
  }, [])

  const logout = (message?: string) => {
    store.remove(JWT_TOKEN_NAME)
    const url = '/login?logout=true'
    if (message) {
      window.location.href = `${url}&message=${encodeURIComponent(message)}`
      return
    }
    window.location.href = '/login?logout=true&message=getUserInfoFailed'
  }

  const { data, isLoading } = useAxios<Partial<IUser>>(
    isAuthenticated ? { url: `/users/me` } : null,
    {
      onError: async (error) => {
        const token = store.get(JWT_TOKEN_NAME) || ''
        try {
          const errorDetails = {
            message: error?.message ?? 'Unknown error',
            status: error?.response?.status ?? 'No status',
            responseData: error?.response?.data ?? 'No response data',
            headers: error?.response?.headers ?? 'No headers',
            requestUrl: error?.config?.url ?? 'No URL',
            requestMethod: error?.config?.method ?? 'No method',
            requestData: error?.config?.data ?? 'No request data',
            timestamp: new Date().toISOString(),
            token,
          }
          await sendToErrorLog(errorDetails)
        } catch (err) {
          console.error('Failed to send error log:', err)
        }
        if (error.response?.status === 401) {
          const refreshToken = store.get(REFRESH_TOKEN_NAME) || ''

          if (refreshToken) {
            try {
              const response = await axiosInstance('/auth/refresh-token', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                data: {
                  refresh_token: refreshToken,
                },
              })
              const newAccessToken = response.data.accessToken
              store.set(JWT_TOKEN_NAME, newAccessToken)
            } catch (err: any) {
              logout(
                `${
                  err?.message ?? 'Session expired, please log in again.'
                } - refresh token error`,
              )
            }
          } else {
            logout(
              `${
                error?.message ?? 'Session expired, please log in again.'
              } with token: ${token ?? '-'}`,
            )
          }
        }
        console.error('Failed to fetch user info:', error)
      },
      onSuccess: (data) => {
        setUserCache(data.data)
      },
      revalidateOnFocus,
    },
  )

  let user = data
  if (!user) {
    const cacheUser = getUserCache()
    user = cacheUser
  }

  const cachedMerchant = getMerchantCache()

  const merchant = useAxios<Partial<IMerchant>>(
    isAuthenticated && !cachedMerchant ? { url: `/merchant/me` } : null,
    {
      revalidateOnFocus,
      onSuccess: (data) => {
        setMerchantCache(data.data)
      },
    },
  )

  const merchantData = merchant?.data || cachedMerchant || null

  return {
    isLoading,
    loading: !data,
    user: {
      ...user,
    },
    merchant: {
      ...merchantData,
    },
    logout,
    isAuthenticated,
  }
}

export const useUserInfo = (username: string, option?: SWRConfiguration) => {
  const isAuthenticated = useMemo(() => {
    return !!store.get(JWT_TOKEN_NAME)
  }, [])

  const { data, isLoading, mutate } = useAxios<IUser>(
    isAuthenticated
      ? {
          url: `/api/users/info/${username}`,
        }
      : null,
    option,
  )
  return {
    data,
    isLoading,
    mutate,
  }
}

export const useMerchant = <T>(config: string) => {
  const isAuthenticated = useMemo(() => {
    return !!store.get(JWT_TOKEN_NAME)
  }, [])

  const { data } = useAxios<T>(
    isAuthenticated ? { url: `/merchant/me`, params: { key: config } } : null,
  )

  return {
    loading: !data,
    config: data,
    isAuthenticated,
  }
}

function setCookie(name: string, value: string, hours: number) {
  const expireDate = new Date()
  expireDate.setHours(expireDate.getHours() + hours)
  document.cookie = `${name}=${encodeURIComponent(
    value,
  )}; expires=${expireDate.toUTCString()}; path=/`
}

// get cookie
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
  return match ? decodeURIComponent(match[2]) : null
}

// remove cookie
function removeCookie(name: string) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
}
